import MersenneTwister from 'mersenne-twister';

/**
 * Returns a seeded random function that returns a float between [0,1), just like Math.random
 */
export function seedRandom(seed) {
  const generator = getSeededMersenneTwister(seed);
  return () => generator.random();
}

/**
 * Returns a seeded random function that returns an integer between [0, 4294967295]
 */
export function seedRandomInt(seed) {
  const generator = getSeededMersenneTwister(seed);
  return () => generator.random_int();
}

export function seedRandomBool(seed) {
  const randomInt = seedRandomInt(seed);
  return () => Boolean(randomInt() & 1);
}

export const getRandomInclusive = (minimum, maximum, randomValue = Math.random()) => {
  const min = Math.floor(minimum);
  const max = Math.floor(maximum);
  return Math.floor(randomValue * (max - min + 1)) + min;
};

/**
 * Takes a String or Number and returns a `MersenneTwister` object with a seed based on that value.
 * This is more convenient than using the constructor for `MersenneTwister` directly, which requires
 * the seed to be either an integer or an array of integers.
 *
 * @param {String|Number} stringOrNumber
 * @returns {MersenneTwister}
 */
function getSeededMersenneTwister(stringOrNumber) {
  let seed;

  if (typeof stringOrNumber === 'string') {
    const string = stringOrNumber;

    // Convert strings to arrays of character code integers.
    seed = [];
    for (let i = 0; i < string.length; i++) {
      seed[i] = string.charCodeAt(i);
    }
  } else if (Number.isFinite(stringOrNumber)) {
    const number = stringOrNumber;

    if (Number.isInteger(number)) {
      seed = number;
    } else {
      // Convert floats to integers. They should be large integers, because MersenneTwister will
      // only use the lower 32 bits of the integer.
      seed = Math.floor(number * Math.pow(2, 32));
    }
  } else {
    throw new Error('Random seed must be a string or number');
  }

  return new MersenneTwister(seed);
}
